import React, { useEffect, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { Banner } from '../ptl-merchant-setup/components/SetupUi'
import { errorText, lookupOdaPincode, makeOdaRegular, saveOdaPincode } from './ptlOdaApi'
import { EDD_TIMING, MAX_EXTRA_DAYS, ODA_TYPES, autoDays, odaRule } from './constants'

const PINCODE = /^[1-9]\d{5}$/

/** What a manual ODA change does, said once in every dialog. */
const EFFECT = `It changes this courier code’s own zone map row: the ODA type drives the courier’s ODA charge; the EDD follows the type, band and days ${EDD_TIMING}. The merchant price does not change. An upload from B2B Couriers replaces the row and clears its band and days.`

const daysText = (n) => `${n} extra day${n === 1 ? '' : 's'}`

/**
 * Add or edit one ODA pincode of a courier code. ODA type and km band are both optional, but one is required. "Extra EDD
 * days" fills in from the type (its days win) or else the band; a different number is saved as days set by hand, which
 * win over both and do not follow later changes on ODA Bands. Add: the pincode must already be in the code's zone map
 * (it needs a zone: ODA zone upload). Every value changed is audited (ODA history).
 */
export function OdaPincodeDialog({ code, bands, row, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar()
  const editing = !!row
  const [pincode, setPincode] = useState(row?.pincode || '')
  const [info, setInfo] = useState(row ? { inZoneMap: true, oda: true, row } : null)
  const [lookupError, setLookupError] = useState('')
  const [odaType, setOdaType] = useState(row?.odaType || '')
  const [band, setBand] = useState(row?.band || '')
  const [days, setDays] = useState(() => {
    if (!row) return ''
    if (row.handDays != null) return String(row.handDays)
    const auto = autoDays(row, bands)
    return auto != null ? String(auto) : ''
  })
  // days kept as set by hand (stored), even when equal to what the type or band gives; a new type or band resets it
  const [byHand, setByHand] = useState(row?.handDays != null)
  const [touched, setTouched] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (editing) return undefined
    setInfo(null)
    setLookupError('')
    if (!PINCODE.test(pincode)) return undefined
    let live = true
    lookupOdaPincode(code, pincode)
      .then((r) => {
        if (!live) return
        setInfo(r)
        const cur = r.row
        setOdaType(cur?.odaType || '')
        setBand(cur?.band || '')
        const auto = cur ? autoDays(cur, bands) : null
        setDays(cur?.handDays != null ? String(cur.handDays) : auto != null ? String(auto) : '')
        setByHand(cur?.handDays != null)
      })
      .catch((e) => live && setLookupError(errorText(e, 'Could not look up the pincode')))
    return () => { live = false }
  }, [code, pincode, editing, bands])

  const auto = autoDays({ odaType, band }, bands)
  const daysNum = days === '' ? null : Number(days)
  const daysBad = days !== '' && (!/^\d{1,2}$/.test(days) || daysNum > MAX_EXTRA_DAYS)
  // typed days equal to what the type or band gives are not stored, so the pincode keeps following ODA Bands; days
  // already set by hand stay set by hand until changed
  const hand = byHand && daysNum !== null
  const neither = !odaType && !band
  const cur = info?.row
  const changed = !cur || (cur.odaType || '') !== odaType || (cur.band || '') !== band || (cur.handDays ?? null) !== (hand ? daysNum : null)
  const canSave = info?.inZoneMap && !neither && !daysBad && changed
  const result = { odaType: odaType || null, band: band || null, handDays: hand ? daysNum : null }

  // a new type or band refills the days with what it gives (an edit by hand is redone after)
  const refill = (nextType, nextBand) => {
    const a = autoDays({ odaType: nextType, band: nextBand }, bands)
    setDays(a != null ? String(a) : '')
    setByHand(false)
  }
  const typeDays = (v) => {
    setDays(v)
    setByHand(v !== '' && Number(v) !== auto)
  }
  const pickType = (v) => { setOdaType(v); setTouched(true); refill(v, band) }
  const pickBand = (v) => { setBand(v); setTouched(true); refill(odaType, v) }

  const save = async () => {
    setBusy(true)
    try {
      const r = await saveOdaPincode({ code, pincode, odaType: odaType || undefined, band: band || undefined, days: hand ? daysNum : undefined })
      onSaved(`${pincode} saved for ${code}: +${r.days} EDD days`)
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not save the ODA pincode'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const daysHelp = daysBad ? `A whole number from 0 to ${MAX_EXTRA_DAYS}`
    : hand ? `Set by hand: adds ${daysText(daysNum)} and wins over the ODA type and band.`
      : auto != null ? `Adds ${daysText(auto)} from the ${odaType ? 'ODA type' : 'band'}. Change it to set the days by hand.`
        : 'Pick an ODA type or a band first.'

  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>{editing ? `Edit ODA pincode ${pincode}` : 'Add an ODA pincode'} · {code}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
          {editing ? (
            <Typography sx={{ ...TYPE.rowHelp }}>
              {[row.city, row.state].filter(Boolean).join(', ') || pincode}{row.zone ? ` · zone ${row.zone}` : ''}
            </Typography>
          ) : (
            <TextField size='small' label='Pincode' value={pincode} autoFocus onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputProps={{ inputMode: 'numeric' }} error={!!pincode && !PINCODE.test(pincode)}
              helperText={pincode && !PINCODE.test(pincode) ? '6 digits' : info?.inZoneMap ? `${[cur?.city, cur?.state].filter(Boolean).join(', ')}${cur?.zone ? ` · zone ${cur.zone}` : ''}${info.oda ? ' · already ODA: you are editing it' : ''}` : ' '}
              sx={{ maxWidth: 360 }} />
          )}

          {lookupError && <Banner tone='danger'>{lookupError}</Banner>}
          {info && !info.inZoneMap && (
            <Banner tone='warn'>{pincode} is not in the zone map of {code}. Give it a zone through the ODA zone upload first.</Banner>
          )}

          {info?.inZoneMap && (
            <>
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField select size='small' label='ODA type (optional)' value={odaType} onChange={(e) => pickType(e.target.value)}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} error={touched && neither} helperText={touched && neither ? 'Pick an ODA type, a band, or both' : 'Courier ODA charge column; its days win over the band'}>
                  <MenuItem value=''>No ODA type</MenuItem>
                  {ODA_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </TextField>
                <TextField select size='small' label='Km band (optional)' value={band} onChange={(e) => pickBand(e.target.value)}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }} error={touched && neither} helperText={odaType && band ? 'Saved, but the ODA type’s days apply' : 'Distance from the delivery hub; EDD only'}>
                  <MenuItem value=''>No band</MenuItem>
                  {(bands?.bands || []).map((b) => <MenuItem key={b.key} value={b.key}>{b.label} · +{b.days}</MenuItem>)}
                </TextField>
              </Box>
              <TextField size='small' label='Extra EDD days' value={days} onChange={(e) => typeDays(e.target.value.replace(/\D/g, '').slice(0, 2))}
                error={daysBad} disabled={neither} inputProps={{ inputMode: 'numeric' }} helperText={daysHelp} sx={{ maxWidth: 360 }} />
              {!neither && (
                <Typography role='status' sx={{ ...TYPE.rowLabel, color: C.inkBody }}>EDD rule: {odaRule(result, bands)}</Typography>
              )}
              <Banner tone='info'>{EFFECT}</Banner>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
        <LoadingButton variant='contained' loading={busy} disabled={!canSave} onClick={save} sx={SX.cta}>{editing || info?.oda ? 'Save' : 'Add ODA pincode'}</LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

/** Takes pincodes of a courier code back to Regular: ODA type Regular, band and days by hand cleared. Audited per value. */
export function RegularDialog({ code, rows, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar()
  const [busy, setBusy] = useState(false)
  const list = rows.map((r) => r.pincode)
  const one = rows.length === 1
  const shown = list.length > 12 ? `${list.slice(0, 12).join(', ')} and ${list.length - 12} more` : list.join(', ')
  const run = async () => {
    setBusy(true)
    try {
      const res = await makeOdaRegular(code, list)
      const done = res.counts?.madeRegular || 0
      onSaved(`${done} pincode${done === 1 ? '' : 's'} made Regular for ${code}${res.warnings?.length ? `. ${res.warnings.join(' ')}` : ''}`, res.warnings?.length ? 'warning' : 'success')
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not make the pincodes Regular'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Dialog open onClose={() => !busy && onClose()} maxWidth='xs' fullWidth>
      <DialogTitle sx={{ ...TYPE.sectionTitle, fontSize: 16 }}>Make {one ? list[0] : `${rows.length} pincodes`} Regular for {code}?</DialogTitle>
      <DialogContent>
        <Typography sx={{ ...TYPE.rowHelp, fontSize: 13.5, color: C.inkBody, mb: 1.5 }}>
          {shown} go{one ? 'es' : ''} to Regular in the {code} zone map: ODA type, km band and days set by hand are cleared. New shipments to {one ? 'it' : 'them'} get no ODA days. Each change is saved in the ODA history.
        </Typography>
        <Banner tone='info'>{EFFECT}</Banner>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none', color: C.inkMuted }}>Cancel</Button>
        <LoadingButton variant='contained' color='error' loading={busy} onClick={run} sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}>
          Make {one ? '' : `${rows.length} `}Regular
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
